import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

export function Account() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [paths, setPaths] = useState([]);
  const [history, setHistory] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [topics, setTopics] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);

  useEffect(() => {
    api.get("/saved-paths").then((r) => setPaths(r.data));
    api.get("/search-history").then((r) => setHistory(r.data));
    api.get("/recurring-struggles").then((r) => setRecurring(r.data));
    api.get("/daily-topics").then((r) => setAllTopics(r.data));
    setTopics(user?.daily_topics || []);
    setSmsPhone(user?.sms_phone || "");
    setSmsConsent(!!user?.sms_consent);
  }, [user]);

  const toggleTopic = (t) => setTopics((c) => c.includes(t) ? c.filter((x) => x !== t) : [...c, t]);
  const saveTopics = async () => { await api.put("/account/daily-topics", { email: user.email, topics }); toast.success("Daily guidance topics updated."); };
  const saveSms = async () => {
    if (smsPhone.trim() && !smsConsent) { toast.error("Please check the consent box to receive text messages."); return; }
    const { data } = await api.put("/account/sms-optin", { phone: smsPhone.trim(), consent: smsConsent });
    setSmsPhone(data.sms_phone); setSmsConsent(data.sms_consent);
    setUser((u) => u ? { ...u, sms_phone: data.sms_phone, sms_consent: data.sms_consent } : u);
    toast.success(data.sms_consent ? "Text message preferences saved." : "Text message opt-in cleared.");
  };
  const openPath = (p) => nav("/wisdom-path", { state: { result: p.data } });
  const delPath = async (id) => { await api.delete(`/saved-paths/${id}`); setPaths((p) => p.filter((x) => x.id !== id)); };
  const deleteAccount = async () => {
    if (!window.confirm("Delete your account and all saved data? This cannot be undone.")) return;
    await api.delete("/account"); setUser(false); toast.success("Account deleted."); nav("/");
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif-display text-4xl text-[#0F172A] mb-1">Account</h1>
      <p className="text-[#475569] mb-8">{user?.name} · {user?.email} · <span className="capitalize">{user?.tier}</span> plan</p>

      <Section title="Choose your daily guidance topics">
        <div className="flex flex-wrap gap-2 mb-4">
          {allTopics.map((t) => (
            <button key={t} data-testid={`topic-${t}`} onClick={() => toggleTopic(t)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${topics.includes(t) ? "bg-[#2C4C3B] text-white" : "bg-[#F4F1EA] text-[#475569]"}`}>{t}</button>
          ))}
        </div>
        <button data-testid="save-topics-btn" onClick={saveTopics} className="px-5 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-medium text-sm">Save topics</button>
      </Section>

      <Section title="Get Proverbs by text">
        <p className="text-sm text-[#475569] mb-4">Add your mobile number to receive Proverbs by text message. This is optional.</p>
        <input data-testid="sms-phone-input" type="tel" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} placeholder="Mobile number (e.g. +1 555 123 4567)"
          className="w-full sm:max-w-sm rounded-lg border border-[#E2E8F0] px-4 py-2.5 focus:outline-none focus:border-[#2C4C3B] mb-4" />
        <label className="flex items-start gap-3 cursor-pointer mb-4">
          <input data-testid="sms-consent-checkbox" type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 accent-[#2C4C3B]" />
          <span className="text-sm text-[#475569] leading-relaxed">
            By checking this box, I give my express consent to receive recurring automated text messages from I Need a Proverb at the number provided. Consent is not a condition of any purchase. Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help. See our <Link to="/privacy" className="text-[#2C4C3B] underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#2C4C3B] underline">Terms of Service</Link>.
          </span>
        </label>
        <button data-testid="save-sms-btn" onClick={saveSms} className="px-5 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-medium text-sm">Save text preferences</button>
        <p className="text-xs text-[#94A3B8] mt-3">Text message delivery is not active yet — your preference is saved for when this feature launches.</p>
      </Section>

      <Section title="Saved Wisdom Paths">
        {paths.length === 0 ? <p className="text-sm text-[#94A3B8]">None saved yet.</p> :
          <div className="space-y-2">{paths.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-[#E2E8F0] px-4 py-3">
              <button onClick={() => openPath(p)} className="text-left text-[#334155] hover:text-[#2C4C3B]">{p.title || "Wisdom Path"} <span className="text-xs text-[#94A3B8]">· {new Date(p.at).toLocaleDateString()}</span></button>
              <button onClick={() => delPath(p.id)} className="text-xs text-[#94A3B8] hover:text-red-600">Remove</button>
            </div>))}</div>}
      </Section>

      <Section title="Recurring struggles">
        {recurring.length === 0 ? <p className="text-sm text-[#94A3B8]">We'll track patterns as you search.</p> :
          <div className="flex flex-wrap gap-2">{recurring.map((r) => <span key={r.slug} className="px-3 py-1.5 rounded-full bg-[#F9F7F1] text-sm text-[#475569]">{r.label} · {r.count}</span>)}</div>}
      </Section>

      <Section title="Search history">
        {history.length === 0 ? <p className="text-sm text-[#94A3B8]">No searches yet.</p> :
          <ul className="space-y-1.5">{history.slice(0, 10).map((h) => (
            <li key={h.id} className="text-sm text-[#475569]">{h.issues.join(", ") || h.situation.slice(0, 60) || "Search"} <span className="text-xs text-[#94A3B8]">· {new Date(h.at).toLocaleDateString()}</span></li>))}</ul>}
      </Section>

      <div className="mt-10 pt-8 border-t border-[#E2E8F0] flex gap-3">
        <button onClick={async () => { await logout(); nav("/"); }} className="px-5 py-2.5 rounded-full border border-[#E2E8F0] text-[#475569]">Sign out</button>
        <button data-testid="delete-account-btn" onClick={deleteAccount} className="px-5 py-2.5 rounded-full text-red-600 border border-red-200 hover:bg-red-50 transition-colors">Delete account</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8 rounded-2xl border border-[#E2E8F0] bg-white p-6">
      <h2 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
}

export function Pricing() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
      <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] mb-3">Always free</p>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] mb-4">I Need a Proverb is free for everyone</h1>
      <p className="text-[#475569] max-w-xl mx-auto leading-relaxed">Every feature is free — unlimited searches, AI situation analysis, the full categorized Proverbs library, saved Wisdom Paths, journaling, personalized guidance, and all downloads. No account tiers, no payments.</p>
      <Link to="/" className="inline-block mt-8 px-8 py-4 rounded-full bg-[#2C4C3B] text-white text-base font-semibold hover:bg-[#1E3629] transition-colors">Find Biblical Wisdom</Link>
    </div>
  );
}
